/**
 * Root navigation container with deep link handling and role based routing.
 */
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { Platform } from 'react-native';
import * as Linking from 'expo-linking';
import {
  NavigationContainer,
  getPathFromState,
  useNavigationContainerRef,
  type LinkingOptions,
  type NavigationState,
  type Theme,
} from '@react-navigation/native';
import { useShallow } from 'zustand/react/shallow';
import { useAuthStore } from '@/common/auth/store';
import { useTenantSettingsStore } from '@/common/context/TenantSettingsStore';
import log from '@/common/utils/logger';
import AuthNavigator from '@/common/navigation/AuthNavigator';
import StudentNavigation from '@/common/navigation/RoleNavigations/StudentNavigation';
import LecturerNavigation from '@/common/navigation/RoleNavigations/Lecturer/LecturerNavigation';
import AdminNavigation from '@/common/navigation/RoleNavigations/AdminNavigation';
import SplashScreen from '@/auth/SplashScreen';
import SystemMessages from '@/common/navigation/components/SystemMessages';
import TrialBar from '@/common/navigation/components/TrialBar';
import type { StudentRouteParamList } from '@/common/navigation/RoleNavigations/Student/types';
import {
  evaluationDeskLinkingScreenConfig,
  flattenEvaluationDeskParamsInState,
  getEvaluationDeskStateFromPath,
} from '@/common/navigation/evaluationDeskLinking';

type RootParamList = StudentRouteParamList & Record<string, object | undefined>;

type DocumentTitleOptions = { title?: string } | undefined;
type DocumentTitleRoute = { name?: string } | undefined;

const navigationBreadcrumbLimit = 5;

/**
 * Builds a URL path from navigation state and strips internal query params.
 */
const getCleanPathFromState = (
  state: NavigationState,
  options?: Parameters<typeof getPathFromState>[1],
) => {
  const path = getPathFromState(flattenEvaluationDeskParamsInState(state), options);
  const [base, query] = path.split('?');
  if (!query) return base;

  const searchParams = new URLSearchParams(query);
  // Remove internal configuration params that should not be stringified in the URL
  searchParams.delete('title');
  searchParams.delete('objectName');
  searchParams.delete('columns');
  searchParams.delete('actions');
  searchParams.delete('data');
  searchParams.delete('action');

  const newQuery = searchParams.toString();
  return newQuery ? `${base}?${newQuery}` : base;
};

/**
 * Formats the document title based on the current route and options.
 */
const formatDocumentTitle = (baseTitle: string, options?: DocumentTitleOptions, route?: DocumentTitleRoute) => {
  const screenTitle = options?.title ?? route?.name;
  return screenTitle ? `${baseTitle} | ${screenTitle}` : baseTitle;
};

const MainNavigation = () => {
  const {
    isAuthenticated,
    user,
    isInitializing,
    setPendingDeepLinkUrl,
    pendingDeepLinkUrl,
  } = useAuthStore(
    useShallow((state) => ({
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      isInitializing: state.isInitializing,
      setPendingDeepLinkUrl: state.setPendingDeepLinkUrl,
      pendingDeepLinkUrl: state.pendingDeepLinkUrl,
    })),
  );

  const { settings, tenantStatus, navigationTheme } = useTenantSettingsStore(useShallow((state) => ({
    settings: state.settings,
    tenantStatus: state.status,
    navigationTheme: state.navigationTheme,
  })));

  const naming = settings?.naming;
  const navigationRef = useNavigationContainerRef<RootParamList>();
  const baseTitle = naming?.displayName || 'L21st Flare';
  const lastRouteNameRef = useRef<string | null>(null);
  const breadcrumbCountRef = useRef(0);

  /**
   * Logs a limited set of navigation breadcrumbs on Android to help diagnose crashes.
   */
  const logNavigationBreadcrumb = useCallback((routeName?: string) => {
    if (Platform.OS !== 'android' || !routeName) return;
    if (breadcrumbCountRef.current >= navigationBreadcrumbLimit) return;
    if (lastRouteNameRef.current === routeName) return;
    breadcrumbCountRef.current += 1;
    lastRouteNameRef.current = routeName;
    log.warn('Navigation state change', { routeName });
  }, []);

  const prefix = Linking.createURL('/');
  const linking = useMemo(() => ({
    prefixes: [prefix],
    config: {
      screens: {
        Login: 'login',
        ForgotPassword: 'forgot-password',
        ResetPassword: {
          path: 'reset-password',
          parse: {
            token: (token: string) => token,
          },
        },
        CourseScreen: {
          path: 'student/courses',
        },
        ModuleScreen: {
          path: 'student/courses/:courseId/modules/:moduleId',
        },
        TaskScreen: {
          path: 'student/courses/:courseId/modules/:moduleId/tasks/:taskId',
          screens: {
            InstructionsPanel: '/instructions',
            ResponseComposer: '/composer',
            ResourceSidebar: '/resources',
            AiTutor: '/ai-tutor',
          },
        },
        GradeScreen: {
          path: 'student/courses/:courseId/modules/:moduleId/grades',
        },
        SlotMeetingScreen: {
          // Optional: reservationless dry-run navigates without a slot id.
          // A required `:slotId` stringifies missing params as the literal
          // "undefined" → `/meeting/slot/undefined` (FLARE-8E / FLARE-8F / FLARE-8P).
          path: 'meeting/slot/:slotId?',
          parse: {
            slotId: (value: string) => {
              if (!value || value === 'undefined' || value === 'null') return undefined;
              return value;
            },
          },
          stringify: {
            slotId: (value: string | undefined) => value ?? '',
          },
        },
        MyConnections: 'student/connections',
        PeopleProfile: 'student/profile',
        // Lecturer Screens
        Engagement: 'lecturer/engagement',
        EvaluationDesk: evaluationDeskLinkingScreenConfig,
        ModuleEditor: {
          path: 'lecturer/modules/editor/:moduleId?/:version?/:taskId?',
        },
        EventCalendar: 'lecturer/calendar',
        ScheduledChanges: {
          path: 'lecturer/scheduled-changes/:changeId?',
        },
        // Admin Screens
        Main: {
          path: 'admin',
          screens: {
            Users: 'users',
            Courses: 'courses',
            Cohorts: 'cohorts',
            Modules: 'modules',
            Approvals: 'approvals',
            Submissions: 'submissions',
            GradingStats: 'insights/grading-stats',
            AiTutor: 'insights/ai-tutor',
          },
        },
        CreateEditForm: {
          path: 'admin/forms/:objectName/:id?',
          screens: {
            details: '',
            modules: 'modules',
            enrollments: 'enrollments',
            grades: 'grades',
            notifications: 'notifications',
            preview: 'preview',
          },
        },
      },
    },
    getPathFromState: getCleanPathFromState,
    getStateFromPath: getEvaluationDeskStateFromPath,
  } as LinkingOptions<RootParamList>), [prefix]);

  // Capture initial deep link URL
  useEffect(() => {
    if (Platform.OS === 'web') {
      const path = window.location.pathname + window.location.search;
      // Only store if it is not an auth related URL
      if (
        path
        && path !== '/'
        && path !== '/login'
        && path !== '/forgot-password'
        && !path.startsWith('/reset-password')
      ) {
        setPendingDeepLinkUrl(path);
      }
    }
  }, [setPendingDeepLinkUrl]);

  // Handle pending deep link after authentication
  useEffect(() => {
    if (Platform.OS === 'web' && isAuthenticated && !isInitializing && navigationRef.isReady() && pendingDeepLinkUrl) {
      // Use getStateFromPath to convert the URL path to a navigation state
      const state = getEvaluationDeskStateFromPath(pendingDeepLinkUrl, linking.config);
      if (state) {
        // Reset the root state to the new deep linked state
        navigationRef.resetRoot(state);
      }
      // Clear the pending deep link
      setPendingDeepLinkUrl(null);
    }
  }, [isAuthenticated, isInitializing, navigationRef, pendingDeepLinkUrl, setPendingDeepLinkUrl, linking.config]);

  // State to track the current window title for React 19 metadata hoisting.
  // This avoids conflicts between React Navigation internal title management and React 19 rendering.
  const [activeTitle, setActiveTitle] = useState(baseTitle);

  const documentTitle = useMemo(
    () => ({
      formatter: (options?: DocumentTitleOptions, route?: DocumentTitleRoute) => (
        formatDocumentTitle(baseTitle, options, route)
      ),
    }),
    [baseTitle],
  );

  /**
   * Resolves the current screen title and updates the activeTitle state.
   */
  const handleStateChange = useCallback(() => {
    if (navigationRef.isReady()) {
      const route = navigationRef.getCurrentRoute();
      const options = navigationRef.getCurrentOptions();
      logNavigationBreadcrumb(route?.name);
      const title = documentTitle.formatter(options, route);
      setActiveTitle(title);
    }
  }, [navigationRef, documentTitle, logNavigationBreadcrumb]);

  useEffect(() => {
    handleStateChange();
  }, [handleStateChange]);

  if (tenantStatus === 'loading' || isInitializing || tenantStatus === 'error') {
    return <SplashScreen />;
  }

  /**
   * Renders the role specific navigator.
   */
  const renderNavigator = () => {
    if (!isAuthenticated) {
      return <AuthNavigator />;
    }

    switch (user?.role) {
      case 'student':
        return <StudentNavigation navigationRef={navigationRef} />;
      case 'lecturer':
        return <LecturerNavigation navigationRef={navigationRef} />;
      case 'admin':
        return <AdminNavigation navigationRef={navigationRef} />;
      default:
        return <AuthNavigator />;
    }
  };

  return (
    <NavigationContainer
      ref={navigationRef}
      linking={linking}
      theme={navigationTheme as Theme}
      documentTitle={documentTitle}
      onReady={handleStateChange}
      onStateChange={handleStateChange}
    >
      <>
        {/* React 19 metadata hoisting: rendering a <title> tag inside the tree
            will automatically move it to the document <head> on Web. */}
        {Platform.OS === 'web' && <title>{activeTitle}</title>}
        <SystemMessages />
        <TrialBar />
        {renderNavigator()}
      </>
    </NavigationContainer>
  );
};

export default MainNavigation;
